import React, { useState, useEffect } from 'react';
import {
  Card,
  CardFooter,
  CardBody,
  Container,
  Row,
  Col,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { withFormik } from 'formik';
import * as Yup from 'yup';
import { withRouter } from 'react-router-dom';
import { Button, Field, useSession } from '@cordata/buffet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { toastSuccess, toastError, adminInstance as axios } from '~/utils';

import useSlice from '~/utils/slice/hook';
import useApi from '~/utils/api/hook';

const formEnhancer = withFormik({
  validationSchema: Yup.object().shape({
    selectedFunctionName: Yup.string().required(),
    patientUid: Yup.mixed().when('selectedFunctionName', {
      is: (f => f === 'merge_facilities' || f === 'merge_providers' || f === 'merge_patients' || f === 'convert_event_times_to_2_0'),
      then: Yup.mixed().notRequired(),
      else: Yup.mixed().required(),
    }),
    p_division_uid_convert: Yup.mixed().when('selectedFunctionName', {
      is: 'convert_event_times_to_2_0',
      then: Yup.mixed().required(),
    }),
    divisionUid: Yup.mixed().when('selectedFunctionName', {
      is: 'move_patient_division',
      then: Yup.mixed().required(),
    }),
    isActive: Yup.string().when('selectedFunctionName', {
      is: 'change_patient_activation',
      then: Yup.string().required(),
    }),
    p_keep_facility_uid: Yup.mixed().when('selectedFunctionName', {
      is: 'merge_facilities',
      then: Yup.mixed().required(),
    }),
    p_lose_facility_uid: Yup.mixed().when('selectedFunctionName', {
      is: 'merge_facilities',
      then: Yup.mixed().required(),
    }),
    p_keep_provider_uid: Yup.mixed().when('selectedFunctionName', {
      is: 'merge_providers',
      then: Yup.mixed().required(),
    }),
    p_lose_provider_uid: Yup.mixed().when('selectedFunctionName', {
      is: 'merge_providers',
      then: Yup.mixed().required(),
    }),
    p_keep_patient_uid: Yup.mixed().when('selectedFunctionName', {
      is: 'merge_patients',
      then: Yup.mixed().required(),
    }),
    p_merge_patient_uid: Yup.mixed().when('selectedFunctionName', {
      is: 'merge_patients',
      then: Yup.mixed().required(),
    }),
    user: Yup.string().required(),
  }),

  handleSubmit: (values, { setStatus, setSubmitting }) => {
    const p = {
      patientUid: values.patientUid,
      ...(values.selectedFunction && values.selectedFunction.value !== 'merge_facilities' && values.selectedFunction.value !== 'merge_providers' && values.selectedFunction.value !== 'merge_patients' && values.selectedFunction.value !== 'convert_event_times_to_2_0' && {
        user: values.user,
      }),
      ...(values.selectedFunction && values.selectedFunction.value === 'move_patient_division' && { divisionUid: values.divisionUid.id }),
      ...(values.selectedFunction && values.selectedFunction.value === 'change_patient_activation' && { isActive: values.isActive }),
      ...(values.selectedFunction && values.selectedFunction.value === 'merge_facilities' && {
        p_keep_facility_uid: values.p_keep_facility_uid.id,
        p_lose_facility_uid: values.p_lose_facility_uid.id,
        p_user_name: values.user,
      }),
      ...(values.selectedFunction && values.selectedFunction.value === 'merge_providers' && {
        'p-keep-provider-uid': typeof values.p_keep_provider_uid === 'object' ? values.p_keep_provider_uid.value : values.p_keep_provider_uid,
        'p-lose-provider-uid': typeof values.p_lose_provider_uid === 'object' ? values.p_lose_provider_uid.value : values.p_lose_provider_uid,
        'p-user-name': values.user,
      }),
      ...(values.selectedFunction && values.selectedFunction.value === 'merge_patients' && {
        'p-keep-patient-uid': values.p_keep_patient_uid,
        'p-merge-patient-uid': values.p_merge_patient_uid,
        'p-user-name': values.user,
      }),
      ...(values.selectedFunction && values.selectedFunction.value === 'convert_event_times_to_2_0' && {
        p_division_uid: values.p_division_uid_convert && values.p_division_uid_convert.id,
        p_user_name: values.user,
      }),
    };

    const url = `/frequent-customer-requests/${values.selectedFunction && values.selectedFunction.value}`;

    setStatus(null);

    axios
      .post(url, p)
      .then((r) => {
        const response = JSON.parse(r.data.data);

        const genericTypeResult = (msg = '') => {
          const text = String(msg);
          if (text === 'nope' || text.includes('ERROR:') || text.includes('Failed')) return 'danger';
          return 'success';
        };

        const providerMergeTypeResult = (msg = '') => {
          const text = String(msg).trim();
          const lower = text.toLowerCase();

          if (lower.startsWith('successfully merged')) return 'success';
          if (lower.startsWith('cannot') || (lower.startsWith('the kept') && lower.includes('cannot'))) return 'warning';
          return 'danger';
        };

        const msg = response.result || response.message || '';

        const isProviderMerge = values?.selectedFunction?.value === 'merge_providers';

        const type = isProviderMerge ? providerMergeTypeResult(msg) : genericTypeResult(msg);

        const s = { ...response, type };

        setStatus(s);

        if (msg === 'yes') {
          toastSuccess();
        } else if (msg === 'nope') {
          toastError();
        } else if (String(msg).includes('Failed') || String(msg).toLowerCase().includes('cannot')) {
          toastError(msg);
        } else {
          toastSuccess(msg);
        }
      })
      .catch(e => setStatus({ type: 'danger', ...e.response.data }))
      .finally(() => setSubmitting(false));
  },

  displayName: 'FrequentCustomerRequestsForm',
  validateOnChange: false,
  validateOnBlur: false,
});

const FCRForm = ({
  values,
  setFieldValue,
  handleSubmit,
  isSubmitting,
  status,
  validateForm,
}) => {
  const { current } = useSession();
  const { fetch } = useSlice();
  const { api } = useApi();

  const [divisions, setDivisions] = useState([]);
  const [organization, setOrganization] = useState({});
  const [division, setDivision] = useState({});
  const [facilities, setFacilities] = useState(null);
  const [organizations, setOrganizations] = useState({});

  const [keepPatient, setKeepPatient] = useState(null);
  const [mergePatient, setMergePatient] = useState(null);
  const [keepProvider, setKeepProvider] = useState(null);
  const [loseProvider, setLoseProvider] = useState(null);
  const [convertDivisions, setConvertDivisions] = useState([]);

  const theDBA = 'Michael Muse';

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const toggleConfirmationModal = () => setIsConfirmationModalOpen(!isConfirmationModalOpen);

  const validate = () =>
    validateForm()
      .then((r) => {
        if (!Object.keys(r).length) {
          toggleConfirmationModal();
        } else {
          toastError();
        }
      });

  const [selectedPatient, setSelectedPatient] = useState();

  const trueFalse = [{
    label: 'True',
    value: 't',
  }, {
    label: 'False',
    value: 'f',
  }];

  const functions = [{
    label: 'Move a patient from one division to another',
    value: 'move_patient_division',
  }, {
    label: 'Deactivate or reactivate a patient',
    value: 'change_patient_activation',
  }, {
    label: 'Deactivate the latest episode for a patient',
    value: 'deactivate_episode',
  }, {
    label: 'Merge Facilities',
    value: 'merge_facilities',
  }, {
    label: 'Merge Providers',
    value: 'merge_providers',
  }, {
    label: 'Merge Patients',
    value: 'merge_patients',
  }, {
    label: 'Convert event times to 2.0',
    value: 'convert_event_times_to_2_0',
  }];

  useEffect(() => {
    if (current) {
      setFieldValue('user', current.attributes.userName);
    }
  }, [current]);

  useEffect(() => {
    setFieldValue('division', null);

    if (values.patientUid) {
      axios
        .get(`summary-patient/${values.patientUid}`)
        .then(r => r.data.data)
        .then((sp) => {
          setSelectedPatient(sp);
        })
        .catch(() => {
          setSelectedPatient(null);
        });
    }
  }, [values.patientUid]);

  useEffect(() => {
    if (values.p_keep_patient_uid) {
      axios
        .get(`summary-patient/${values.p_keep_patient_uid}`)
        .then(r => r.data.data)
        .then((sp) => {
          setKeepPatient(sp);
        })
        .catch(() => {
          setKeepPatient(null);
        });
    } else {
      setKeepPatient(null);
    }
  }, [values.p_keep_patient_uid]);

  useEffect(() => {
    if (values.p_merge_patient_uid) {
      axios
        .get(`summary-patient/${values.p_merge_patient_uid}`)
        .then(r => r.data.data)
        .then((sp) => {
          setMergePatient(sp);
        })
        .catch(() => {
          setMergePatient(null);
        });
    } else {
      setMergePatient(null);
    }
  }, [values.p_merge_patient_uid]);

  useEffect(() => {
    setFieldValue('selectedFunctionName', values.selectedFunction ? values.selectedFunction.value : null);

    if (values.selectedFunction && (values.selectedFunction.value === 'merge_facilities' || values.selectedFunction.value === 'merge_providers' || values.selectedFunction.value === 'convert_event_times_to_2_0')) {
      fetch('organization')
        .then(({ data: { data: r } }) => setOrganizations(r));
    }
  }, [values.selectedFunction]);

  useEffect(() => {
    setFacilities();

    if (values.organization && values.selectedFunction.value === 'merge_facilities') {
      api.get('/facility', {
        params: {
          'all-data': '1', sort: 'facilityName', 'org-uid': values.organization.id,
        },
      })
        .then(({ data: { data: r } }) => setFacilities(r));
    }

    if (values.organization && values.selectedFunction.value === 'convert_event_times_to_2_0') {
      fetch('division', { 'org-uid': values.organization.id })
        .then(({ data: { data: r } }) => setConvertDivisions(r));
    }
  }, [values.organization]);

  useEffect(() => {
    if (selectedPatient) {
      fetch('organization', {
        id: selectedPatient.attributes.orgUid,
      })
        .then(r => r.data.data)
        .then(setOrganization);

      fetch('division', {
        id: selectedPatient.attributes.divisionUid,
      })
        .then(r => r.data.data)
        .then(setDivision);

      fetch('division', {
        'org-uid': selectedPatient.attributes.orgUid,
      })
        .then(r => r.data.data)
        .then((r) => {
          setDivisions(r.map(d => ({
            ...d,
            isDisabled: d.id === selectedPatient.attributes.divisionUid,
          })));
        });
    }
  }, [selectedPatient]);

  return (
    <Card>
      <form onSubmit={handleSubmit} noValidate>
        <CardBody>
          {status && (
            <Alert color={status.type}>
              <p>
                Result: {status.result || status.message}
              </p>

              {status.type === 'success' && (
                <p>
                  The function ran successfully!{' '}

                  {values.selectedFunction && values.selectedFunction.value === 'move_patient_division' && (
                    <span>
                      {`${selectedPatient.attributes.firstName} ${selectedPatient.attributes.lastName}`} has been moved to {organization.attributes.orgName} - {values.divisionUid.attributes.divisionName}.
                    </span>
                  )}

                  {values.selectedFunction && values.selectedFunction.value === 'change_patient_activation' && (
                    <span>
                      {selectedPatient ? (
                        <>
                          {`${selectedPatient.attributes.firstName} ${selectedPatient.attributes.lastName}`} has had all of {selectedPatient.attributes.genderLabel.toLowerCase().includes('f') ? 'her' : 'his'} records {values.isActive === 'f' && 'de'}activated.
                        </>
                      ) : (
                        <>
                          Patient has had all of their records activated.
                        </>
                    )}
                    </span>
                  )}

                  {values.selectedFunction && values.selectedFunction.value === 'deactivate_episode' && (
                    <span>
                      {selectedPatient && `${selectedPatient.attributes.firstName} ${selectedPatient.attributes.lastName}`} has had {selectedPatient.attributes.genderLabel.toLowerCase().includes('f') ? 'her' : 'his'} latest episode deactivated.
                    </span>
                  )}

                  {values.selectedFunction && values.selectedFunction.value === 'merge_patients' && keepPatient && mergePatient && (
                    <span>
                      {mergePatient && `${mergePatient.attributes.firstName} ${mergePatient.attributes.lastName}`} has been successfully merged into {`${keepPatient.attributes.firstName} ${keepPatient.attributes.lastName}`}.
                    </span>
                  )}

                  {values.selectedFunction && values.selectedFunction.value === 'merge_providers' && (
                    <span>
                      Provider has been successfully merged.
                    </span>
                  )}

                  {values.selectedFunction && values.selectedFunction.value === 'convert_event_times_to_2_0' && (
                    <span>
                      Event times for the selected division have been converted to 2.0.
                    </span>
                  )}

                  {' '}If it seems like this is not the case, please contact {theDBA}.
                </p>
              )}

              {status.type === 'danger' && (
                <p>
                  Something went wrong. Please contact {theDBA} for more details.
                </p>
              )}
            </Alert>
          )}

          <Field
            name="selectedFunction"
            label="Function"
            placeholder="Select a function ..."
            type="select"
            options={functions}
            required
          />

          {values.selectedFunction && values.selectedFunction.value === 'move_patient_division' && (
            <>
              <Alert color="info">Note: You cannot move a patient to a division outside of the org that they are currently in. You also cannot move a patient to a division where the person they are associated with already exists (in other words, another division that the patient is already in, if they are present in more than one division in the org).</Alert>
            </>
          )}

          {values.selectedFunction && values.selectedFunction.value === 'deactivate_episode' && (
            <>
              <Alert color="info">Note: You can only deactivate the latest episode a patient has. You cannot deactivate an episode if it is the only episode a patient has, or if the episode contains more than one event. If either of those is the case for an episode you need to deactivate, please contact {theDBA}.</Alert>
            </>
          )}

          {values.selectedFunction && (values.selectedFunction.value !== 'merge_facilities' && values.selectedFunction.value !== 'merge_providers' && values.selectedFunction.value !== 'merge_patients' && values.selectedFunction.value !== 'convert_event_times_to_2_0') && (
            <>
              <Field
                name="patientUid"
                required
              />

              {selectedPatient && (
              <>
                <Alert color="info">
                  This is the patient_uid for {`${selectedPatient.attributes.firstName} ${selectedPatient.attributes.lastName}`}.

                  {values.selectedFunction && values.selectedFunction.value === 'move_patient_division' && organization.attributes && division.attributes &&
                  <span> {`${selectedPatient.attributes.firstName} ${selectedPatient.attributes.lastName}`} is currently in {organization.attributes.orgName} - {division.attributes.divisionName}.</span>
                }

                  {values.selectedFunction && values.selectedFunction.value === 'deactivate_episode' &&
                  <span> Running this function will deactivate the latest episode for {`${selectedPatient.attributes.firstName} ${selectedPatient.attributes.lastName}`} if {`${selectedPatient.attributes.firstName} ${selectedPatient.attributes.lastName}`} has more than one episode and the latest episode has only one event.</span>
                }
                </Alert>
              </>
            )}
            </>
          )}

          {values.selectedFunction && (values.selectedFunction.value === 'merge_facilities' || values.selectedFunction.value === 'merge_providers' || values.selectedFunction.value === 'convert_event_times_to_2_0') && (
            <Field
              name="organization"
              type="select"
              options={organizations}
              required
              getOptionValue={o => o.id}
              getOptionLabel={o => o.attributes.orgName}
            />
          )}

          {values.selectedFunction && values.selectedFunction.value === 'merge_providers' && (
            <>
              <Alert color="warning">
                <strong>Warning:</strong> Merging providers is permanent and cannot be undone.
                All data from the merged provider will be transferred to the base provider,
                and the merged provider record will be set to is_in_selections=false.
              </Alert>

              <Row form>
                <Col sm>
                  <Field
                    name="p_keep_provider_uid"
                    label="Base Provider (KEEP)"
                    placeholder="Search for provider to keep..."
                    type="select"
                    async
                    loadOptions={(input) =>
                      (values.organization ? axios
                        .get('/summary-provider', { params: { 'display-name': input, 'org-uid': values.organization.id, sort: 'lastname,display-name' } })
                        .then(r => r.data.data)
                        .then(r =>
                          r.map(provider => ({
                            label: `${provider.attributes.displayName} ${provider.attributes.providerRoleLabel ? `(${provider.attributes.providerRoleLabel})` : ''}`,
                            value: provider.id,
                            provider,
                          }))) : Promise.resolve([]))
                    }
                    onChange={(value) => {
                      setFieldValue('p_keep_provider_uid', value ? value.value : null);
                      setKeepProvider(value ? value.provider : null);
                    }}
                    required
                    text="This provider will receive all data from the merged provider"
                    noOptionsMessage={(input) => (!input.inputValue || !input.inputValue.length ? 'Type to search for providers...' : 'No providers found')}
                    disabled={!values.organization}
                  />

                  {keepProvider && (
                    <Alert color="info">
                      <strong>Base Provider:</strong> {keepProvider.attributes.displayName}
                      <br />
                      {keepProvider.attributes.providerRoleLabel && (
                        <>
                          <strong>Role:</strong> {keepProvider.attributes.providerRoleLabel}
                          <br />
                        </>
                      )}
                      {keepProvider.attributes.npiNumber && (
                        <>
                          <strong>NPI:</strong> {keepProvider.attributes.npiNumber}
                          <br />
                        </>
                      )}
                    </Alert>
                  )}
                </Col>

                <Col sm>
                  <Field
                    name="p_lose_provider_uid"
                    label="Provider to Merge (REMOVE)"
                    placeholder="Search for provider to merge..."
                    type="select"
                    async
                    loadOptions={(input) =>
                      (values.organization ? axios
                        .get('/summary-provider', { params: { 'display-name': input, 'org-uid': values.organization.id, sort: 'lastname,display-name' } })
                        .then(r => r.data.data)
                        .then(r =>
                          r.map(provider => ({
                            label: `${provider.attributes.displayName} ${provider.attributes.providerRoleLabel ? `(${provider.attributes.providerRoleLabel})` : ''}`,
                            value: provider.id,
                            provider,
                          }))) : Promise.resolve([]))
                    }
                    onChange={(value) => {
                      setFieldValue('p_lose_provider_uid', value ? value.value : null);
                      setLoseProvider(value ? value.provider : null);
                    }}
                    required
                    text="This provider's data will be moved to the base provider"
                    noOptionsMessage={(input) => (!input.inputValue || !input.inputValue.length ? 'Type to search for providers...' : 'No providers found')}
                    disabled={!values.organization}
                  />

                  {loseProvider && (
                    <Alert color="info">
                      <strong>Provider to Merge:</strong> {loseProvider.attributes.displayName}
                      <br />
                      {loseProvider.attributes.providerRoleLabel && (
                        <>
                          <strong>Role:</strong> {loseProvider.attributes.providerRoleLabel}
                          <br />
                        </>
                      )}
                      {loseProvider.attributes.npiNumber && (
                        <>
                          <strong>NPI:</strong> {loseProvider.attributes.npiNumber}
                          <br />
                        </>
                      )}
                    </Alert>
                  )}
                </Col>
              </Row>

              {keepProvider && loseProvider && (
                <>
                  {values.p_keep_provider_uid === values.p_lose_provider_uid ? (
                    <Alert color="danger">
                      <strong>Error:</strong> You cannot merge a provider into itself.
                      Please select two different providers.
                    </Alert>
                  ) : (
                    <Alert color="warning">
                      <strong>Confirm:</strong> You are about to merge{' '}
                      {loseProvider.attributes.displayName} into{' '}
                      {keepProvider.attributes.displayName}.
                      <br />
                      All care events, patients, signatures, locations, and other data will be
                      transferred.
                    </Alert>
                  )}
                </>
              )}
            </>
          )}

          {values.selectedFunction && values.selectedFunction.value === 'merge_facilities' &&
            <Row form>
              <Col sm>
                <Field
                  name="p_keep_facility_uid"
                  label="Base Facility"
                  placeholder="Select a facility to merge to ..."
                  type="select"
                  options={facilities}
                  required
                  text="this facility will be the base of the merge"
                  getOptionValue={o => o.id}
                  getOptionLabel={o => o.attributes.facilityName}
                  disabled={!values.organization}
                />
              </Col>

              <Col sm>
                <Field
                  name="p_lose_facility_uid"
                  label="Facility being merged into the base"
                  placeholder="Select one facility to merge from ..."
                  type="select"
                  options={facilities && facilities.map(f => ({
                    ...f,
                    isDisabled: values.p_keep_facility_uid &&
                      values.p_keep_facility_uid.id === f.id,
                  }))}
                  required
                  text="this facility will go into the previous facility"
                  getOptionValue={o => o.id}
                  getOptionLabel={o => o.attributes.facilityName}
                  disabled={!values.organization}
                />
              </Col>
            </Row>
          }

          {values.selectedFunction && values.selectedFunction.value === 'convert_event_times_to_2_0' && (
            <>
              <Alert color="info">
                This function converts event times for all care events in a division from 1.0 to
                2.0 timezone format. The division must belong to an org that has at least one user
                with the <strong>hasAppCordataWeb</strong> permission. If no such user exists, the
                function will not run.
              </Alert>

              <Field
                name="p_division_uid_convert"
                label="Division"
                placeholder="Select a division ..."
                type="select"
                options={convertDivisions}
                required
                getOptionValue={o => o.id}
                getOptionLabel={o => o.attributes.divisionName}
                disabled={!values.organization}
              />
            </>
          )}

          {values.selectedFunction && values.selectedFunction.value === 'merge_patients' && (
            <>
              <Alert color="warning">
                <strong>Warning:</strong> Merging patients is permanent and cannot be undone.
                All data from the merged patient will be transferred to the base patient,
                and the merged patient record will be deactivated.
              </Alert>

              <Row form>
                <Col sm>
                  <Field
                    name="p_keep_patient_uid"
                    label="Base Patient UID (KEEP)"
                    placeholder="Enter patient UID to keep..."
                    type="text"
                    required
                    text="This patient will receive all data from the merged patient"
                  />

                  {keepPatient && (
                    <Alert color="info">
                      This is the patient_uid for {`${keepPatient.attributes.firstName} ${keepPatient.attributes.lastName}`}.
                      <br />
                      <strong>DOB:</strong> {keepPatient.attributes.birthDate}
                      <br />
                      <strong>Division:</strong> {keepPatient.attributes.divisionName}
                    </Alert>
                  )}
                </Col>

                <Col sm>
                  <Field
                    name="p_merge_patient_uid"
                    label="Patient UID to Merge (REMOVE)"
                    placeholder="Enter patient UID to merge..."
                    type="text"
                    required
                    text="This patient's data will be moved to the base patient and deactivated"
                  />

                  {mergePatient && (
                    <Alert color="info">
                      This is the patient_uid for {`${mergePatient.attributes.firstName} ${mergePatient.attributes.lastName}`}.
                      <br />
                      <strong>DOB:</strong> {mergePatient.attributes.birthDate}
                      <br />
                      <strong>Division:</strong> {mergePatient.attributes.divisionName}
                    </Alert>
                  )}
                </Col>
              </Row>

              {keepPatient && mergePatient && (
                <>
                  {values.p_keep_patient_uid === values.p_merge_patient_uid ? (
                    <Alert color="danger">
                      <strong>Error:</strong> You cannot merge a patient into itself.
                      Please select two different patients.
                    </Alert>
                  ) : (
                    <Alert color="warning">
                      <strong>Confirm:</strong> You are about to merge {`${mergePatient.attributes.firstName} ${mergePatient.attributes.lastName}`} into {`${keepPatient.attributes.firstName} ${keepPatient.attributes.lastName}`}.
                      <br />
                      All episodes, events, notes, and other data will be transferred.
                    </Alert>
                  )}
                </>
              )}
            </>
          )}

          {values.selectedFunction && values.selectedFunction.value === 'move_patient_division' && selectedPatient && organization.attributes && divisions && (
            <>
              <Field
                name="divisionUid"
                label="Division to move to"
                type="select"
                options={divisions}
                getOptionValue={o => o.id}
                getOptionLabel={o => o.attributes.divisionName}
                required
              />

              {values.divisionUid && (
                <Alert color="info">Running this function will move {`${selectedPatient.attributes.firstName} ${selectedPatient.attributes.lastName}`} to {organization.attributes.orgName} - {values.divisionUid && values.divisionUid.attributes.divisionName}.</Alert>
            )}
            </>
          )}

          {values.selectedFunction && values.selectedFunction.value === 'change_patient_activation' && (
            <>
              <Field
                name="isActive"
                label="Set patient is_active to"
                type="radio-group"
                options={trueFalse}
                required
              />

              {selectedPatient && values.isActive === 't' && (
                <Alert color="info">Running this function will set is_active to TRUE for all records associated with {`${selectedPatient.attributes.firstName} ${selectedPatient.attributes.lastName}`}. This means that even if there was an event or episode or anything that was previously deactivated for any reason, i.e. at the request of a customer, it will now be reactivated.</Alert>
            )}

              {selectedPatient && values.isActive === 'f' && (
                <Alert color="info">Running this function will set is_active to FALSE for all records associated with {`${selectedPatient.attributes.firstName} ${selectedPatient.attributes.lastName}`}.</Alert>
            )}
            </>
          )}

          <Field
            name="user"
            label="You are running this as:"
            disabled
          />
        </CardBody>

        <CardFooter className="d-flex">
          <div className="ml-auto">
            <Button
              type="button"
              color="primary"
              onClick={validate}
              disabled={
                isSubmitting ||
                !values.selectedFunction ||
                (values.selectedFunction.value === 'merge_patients' &&
                  values.p_keep_patient_uid === values.p_merge_patient_uid) ||
                (values.selectedFunction.value === 'merge_providers' &&
                  values.p_keep_provider_uid === values.p_lose_provider_uid)
              }
            >
              Run{isSubmitting ? 'ning ...' : ''}
            </Button>
          </div>
        </CardFooter>

        <Modal isOpen={isConfirmationModalOpen} backdrop="static">
          <ModalHeader>Are you sure you want to continue?</ModalHeader>

          <ModalBody>
            <Container>
              <Row>
                <Col md={2} className="m-auto">
                  <FontAwesomeIcon icon="exclamation-triangle" size="3x" className="text-secondary" />
                </Col>

                <Col>
                  If you are not sure, hit cancel and confirm that everything
                  is correct. Otherwise, hit continue.
                </Col>
              </Row>
            </Container>
          </ModalBody>

          <ModalFooter>
            <Button type="cancel" onClick={toggleConfirmationModal}>Cancel</Button>

            <Button onClick={(e) => { toggleConfirmationModal(); handleSubmit(e); }}>
              Continue
            </Button>
          </ModalFooter>
        </Modal>
      </form>
    </Card>
  );
};

export default withRouter(formEnhancer(FCRForm));
